import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Wrench, GitMerge, ListOrdered, Database, ShieldAlert } from "lucide-react";

const rupee = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

export default function BudgetAdminTools() {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [pid, setPid] = useState("");
  const [heads, setHeads] = useState([]);
  const [subtypes, setSubtypes] = useState([]);
  const [selectedHead, setSelectedHead] = useState("");
  const [mergeOpen, setMergeOpen] = useState(null); // source subtype
  const [migration, setMigration] = useState(null);

  useEffect(() => {
    api.get("/projects").then(r => {
      const codified = r.data.filter(p => p.project_code);
      setProjects(codified);
      if (codified[0]) setPid(codified[0].id);
    });
  }, []);

  const loadHeadsSubs = () => {
    if (!pid) return;
    Promise.all([
      api.get(`/projects/${pid}/heads`),
      api.get(`/projects/${pid}/subtypes?include_disabled=true`),
    ]).then(([h, s]) => { setHeads(h.data); setSubtypes(s.data); });
  };
  useEffect(() => { loadHeadsSubs(); }, [pid]);

  const loadMigration = () => {
    if (!pid) return;
    api.get(`/v2/migration/legacy-preview?project_id=${pid}`)
      .then(r => setMigration(r.data))
      .catch(err => toast.error(err.response?.data?.detail || "Preview failed"));
  };

  const runRenumber = async (headId) => {
    if (!confirm("Renumber every active sub-type under this head sequentially (01, 02, …). Archived rows get an _ARCHIVED suffix. Continue?")) return;
    try {
      const r = await api.post(`/projects/${pid}/heads/${headId}/renumber-subtypes`);
      toast.success(`Renumbered ${r.data.changes?.length || 0} sub-types`);
      loadHeadsSubs();
    } catch (err) { toast.error(err.response?.data?.detail || "Renumber failed"); }
  };

  const commitMigration = async () => {
    if (!migration || !migration.rows.length) return;
    const mappings = migration.rows
      .filter(r => r.suggested_standard_head_code && r.status !== "unmatched")
      .map(r => ({
        legacy_head_id: r.legacy_head_id,
        standard_head_code: r.suggested_standard_head_code,
        subtypes: r.subtypes.map(s => ({
          legacy_subtype_id: s.legacy_subtype_id,
          sanctioned_amount: s.sanctioned_amount,
          working_amount: s.working_amount,
        })),
      }));
    if (!mappings.length) { toast.error("No confident mappings to commit"); return; }
    if (!confirm(`Commit migration of ${mappings.length} legacy heads to codified project_heads / project_subtypes?`)) return;
    try {
      const r = await api.post("/v2/migration/commit", { project_id: pid, mappings });
      toast.success(`Created ${r.data.created_heads} heads + ${r.data.created_subtypes} sub-types · re-pointed ${r.data.remapped_expenses + r.data.remapped_reimbursements} rows`);
      loadHeadsSubs();
      loadMigration();
    } catch (err) { toast.error(err.response?.data?.detail || "Commit failed"); }
  };

  if (user && user.role !== "Admin") {
    return (
      <div className="p-8 max-w-2xl mx-auto">
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-8 text-center">
            <ShieldAlert className="w-12 h-12 mx-auto text-amber-500 mb-3" />
            <div className="font-semibold">Admin only</div>
            <p className="text-sm text-slate-500 mt-1">Budget Admin Tools are restricted to Admin role.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const currentProject = projects.find(p => p.id === pid);
  const subsForHead = selectedHead ? subtypes.filter(s => s.head_id === selectedHead) : [];
  const mergeCandidates = mergeOpen ? subtypes.filter(s => s.head_id === mergeOpen.head_id && s.id !== mergeOpen.id) : [];

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="budget-admin-tools-page">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Wrench className="w-6 h-6" /> Budget Admin Tools</h1>
          <p className="text-sm text-slate-500 mt-1">Merge sub-types · Renumber codes · Legacy migration (Phase 3)</p>
        </div>
        <div className="w-72">
          <Select value={pid} onValueChange={setPid}>
            <SelectTrigger data-testid="admin-project-picker"><SelectValue placeholder="Select project" /></SelectTrigger>
            <SelectContent>{projects.map(p => <SelectItem key={p.id} value={p.id}>{p.project_code} · {p.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      {/* Merge + Renumber */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base"><GitMerge className="w-4 h-4" /> Sub-Type Merge & Renumber</CardTitle>
          <div className="flex items-center gap-2">
            <Select value={selectedHead} onValueChange={setSelectedHead}>
              <SelectTrigger className="w-72" data-testid="admin-head-picker"><SelectValue placeholder="Pick head" /></SelectTrigger>
              <SelectContent>{heads.map(h => <SelectItem key={h.id} value={h.id}>{h.code} · {h.name}</SelectItem>)}</SelectContent>
            </Select>
            {selectedHead && (
              <Button variant="outline" onClick={() => runRenumber(selectedHead)} data-testid="admin-renumber-btn">
                <ListOrdered className="w-4 h-4 mr-1" /> Renumber
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {!selectedHead ? (
            <div className="text-sm text-slate-500 p-6 text-center border border-dashed rounded-md">Pick a head to see its sub-types.</div>
          ) : subsForHead.length === 0 ? (
            <div className="text-sm text-slate-500 p-6 text-center">No sub-types on this head.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-xs text-slate-500 border-b">
                <tr>
                  <th className="text-left py-2 px-2">Code</th>
                  <th className="text-left py-2 px-2">Name</th>
                  <th className="text-right py-2 px-2">Sanctioned</th>
                  <th className="text-right py-2 px-2">Spent</th>
                  <th className="text-right py-2 px-2">Pending</th>
                  <th className="text-right py-2 px-2">Balance</th>
                  <th className="text-center py-2 px-2">Status</th>
                  <th className="text-right py-2 px-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {subsForHead.map(s => (
                  <tr key={s.id} className="border-b hover:bg-slate-50" data-testid={`admin-sub-row-${s.code}`}>
                    <td className="py-2 px-2 font-mono text-xs">{s.code}</td>
                    <td className="py-2 px-2">{s.name}{s.is_name_locked && <Badge className="ml-2 bg-amber-100 text-amber-700 text-[10px]">locked</Badge>}</td>
                    <td className="py-2 px-2 text-right font-mono">{rupee(s.sanctioned_amount)}</td>
                    <td className="py-2 px-2 text-right font-mono text-rose-600">{rupee(s.spent_amount)}</td>
                    <td className="py-2 px-2 text-right font-mono text-amber-600">{rupee(s.pending_amount)}</td>
                    <td className="py-2 px-2 text-right font-mono text-emerald-700">{rupee(s.balance_amount)}</td>
                    <td className="py-2 px-2 text-center"><Badge variant={s.status === "active" ? "default" : "secondary"} className="text-[10px]">{s.status}</Badge></td>
                    <td className="py-2 px-2 text-right">
                      <Button size="sm" variant="outline" onClick={() => setMergeOpen(s)} data-testid={`admin-merge-btn-${s.code}`}>
                        <GitMerge className="w-3 h-3 mr-1" /> Merge…
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Migration */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base"><Database className="w-4 h-4" /> Legacy → Codified Migration (Phase 3)</CardTitle>
          <Button variant="outline" onClick={loadMigration} disabled={!pid} data-testid="admin-migration-preview-btn">
            Preview mapping
          </Button>
        </CardHeader>
        <CardContent>
          {!migration ? (
            <div className="text-sm text-slate-500 p-6 text-center border border-dashed rounded-md">
              Click <b>Preview mapping</b> to see legacy head_master + sub_types rows for {currentProject?.name || "this project"}.
            </div>
          ) : migration.rows.length === 0 ? (
            <div className="p-6 text-center">
              <div className="text-sm text-slate-500">No un-migrated legacy heads for this project.</div>
              <div className="text-xs text-slate-400 mt-1">Total legacy heads scanned: {migration.counts.heads} · sub-types: {migration.counts.subtypes}</div>
            </div>
          ) : (
            <>
              <div className="flex gap-2 mb-3 text-xs">
                <Badge variant="outline">{migration.counts.heads} heads</Badge>
                <Badge variant="outline">{migration.counts.subtypes} sub-types</Badge>
                <Badge className="bg-emerald-100 text-emerald-700">{migration.counts.auto} auto-mapped</Badge>
                {migration.counts.unmatched > 0 && <Badge className="bg-rose-100 text-rose-700">{migration.counts.unmatched} unmatched</Badge>}
              </div>
              <table className="w-full text-sm">
                <thead className="text-xs text-slate-500 border-b">
                  <tr>
                    <th className="text-left py-2 px-2">Legacy Head</th>
                    <th className="text-left py-2 px-2">Suggested Standard</th>
                    <th className="text-center py-2 px-2">Confidence</th>
                    <th className="text-right py-2 px-2">Sub-types</th>
                  </tr>
                </thead>
                <tbody>
                  {migration.rows.map(r => (
                    <tr key={r.legacy_head_id} className="border-b" data-testid={`migration-row-${r.legacy_head_id}`}>
                      <td className="py-2 px-2">{r.legacy_name}</td>
                      <td className="py-2 px-2 font-mono text-xs">{r.suggested_standard_head_code || "—"}</td>
                      <td className="py-2 px-2 text-center">
                        <Badge className={`text-[10px] ${r.status === "auto" ? "bg-emerald-100 text-emerald-700" : r.status === "needs_confirmation" ? "bg-amber-100 text-amber-700" : "bg-rose-100 text-rose-700"}`}>
                          {r.confidence}% · {r.status}
                        </Badge>
                      </td>
                      <td className="py-2 px-2 text-right font-mono">{r.subtypes.length}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="mt-4 flex justify-end">
                <Button onClick={commitMigration} data-testid="admin-migration-commit-btn" className="bg-[#0F172A]">
                  Commit Migration
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Merge Dialog */}
      <Dialog open={!!mergeOpen} onOpenChange={(o) => !o && setMergeOpen(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Merge sub-type into…</DialogTitle></DialogHeader>
          {mergeOpen && (
            <MergeForm
              source={mergeOpen}
              candidates={mergeCandidates}
              onDone={() => { setMergeOpen(null); loadHeadsSubs(); }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MergeForm({ source, candidates, onDone }) {
  const [target, setTarget] = useState("");
  const [busy, setBusy] = useState(false);

  const doMerge = async () => {
    if (!target) return;
    if (!confirm(`Merge "${source.code} · ${source.name}" into the selected sub-type? The source will be hard-deleted, all expenses re-pointed. This cannot be undone.`)) return;
    setBusy(true);
    try {
      const r = await api.post(`/project-subtypes/${source.id}/merge-into`, { target_subtype_id: target });
      const rm = r.data.remapped;
      toast.success(`Merged. Re-pointed ${(rm.expenses_v2 || 0) + (rm.reimbursements || 0) + (rm.expenses_legacy || 0)} rows.`);
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Merge failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3">
      <div className="text-sm">
        <div className="text-slate-500 text-xs">Source</div>
        <div className="font-mono">{source.code} · {source.name}</div>
        <div className="text-xs text-slate-500 mt-1">Sanctioned {rupee(source.sanctioned_amount)} · Spent {rupee(source.spent_amount)} · Balance {rupee(source.balance_amount)}</div>
      </div>
      <div>
        <div className="text-xs text-slate-500 mb-1">Target sub-type (must be on same head)</div>
        <Select value={target} onValueChange={setTarget}>
          <SelectTrigger data-testid="merge-target-picker"><SelectValue placeholder="Pick target sub-type" /></SelectTrigger>
          <SelectContent>{candidates.map(c => <SelectItem key={c.id} value={c.id}>{c.code} · {c.name} (bal {rupee(c.balance_amount)})</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <DialogFooter>
        <Button onClick={doMerge} disabled={!target || busy} className="bg-rose-600 hover:bg-rose-700" data-testid="merge-confirm-btn">
          {busy ? "Merging…" : "Merge & Delete Source"}
        </Button>
      </DialogFooter>
    </div>
  );
}
